import { AutoFixHigh, ChevronRight, CreateOutlined } from "@mui/icons-material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import * as MUI from "@mui/material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import { APPOINTMENT_AUDIT } from "../../../../enums/QueryKeys";
import { SINGLE_WAREHOUSE_ITEM } from "../../../../enums/UrlPaths";
import { fetchAppointment } from "../../../../enums/queryFunction/Fetch";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import AppointmentSingleRevisionDetails from "./AppointmentSingleRevisionDetails";

// Strips HTML tags and replaces them with a space to prevent words from merging.
// Also collapses multiple spaces and trims the result for a clean output.
const stripHtml = (str) =>
  typeof str === "string"
    ? str
        .replace(/<\/?[^>]+(>|$)/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : str;

const getVaccinations = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((v) => v?.vaccinationCategory);
  if (value.vaccinations) return value.vaccinations;
  if (value.vaccinationCategory) return [value];
  return [];
};

function truncateString(str, t) {
  if (str === "" || str == null) {
    return t("Nothing");
  }

  let maybeObj;

  if (typeof str === "object") {
    maybeObj = str;
  } else if (typeof str === "string") {
    try {
      maybeObj = JSON.parse(str);
    } catch (e) {
      maybeObj = undefined; // not JSON, move on
    }
  }

  // If it's an object, check if it's date-like
  if (maybeObj && typeof maybeObj === "object") {
    if (isLocalDateTimeLike(maybeObj)) {
      const formatted = formatLocalDateTime(maybeObj);
      if (formatted) return formatted;
    }
    const vacs = getVaccinations(str);
    if (vacs.length > 0) {
      return t(
        vacs.length === 1
          ? "AddedVaccinationsCount"
          : "AddedVaccinationsCountPlural",
        { count: vacs.length },
      );
    }
    // generic object
    const json = JSON.stringify(maybeObj);
    return json.length > 80 ? `${json.slice(0, 50)}...` : t(json);
  }

  // plain string
  if (typeof str === "string") {
    return str.length > 80 ? `${str.slice(0, 50)}...` : t(str);
  }

  // fallback for numbers / booleans
  return t(String(str));
}

const formatChange = (field, value) => {
  if (typeof value === "object" && value !== null) {
    var formattedDate = null;
    if (field == "endDate" || field == "startDate") {
      formattedDate = dayjs(
        new Date(value[0], value[1] - 1, value[2], value[3], value[4]),
      ).format("DD/MM/YYYY HH:mm");
    }
    switch (field) {
      case "user":
        return value?.fullName;
      case "biometrics":
        return "AddedBiometrics";
      case "priceList":
        return value?.title;
      case "pet":
        return value?.name;
      case "treatment":
        if (value?.description != null) {
          return value?.description?.replace(/<\/?[^>]+(>|$)/g, "");
        }
        return value?.description;
      case "endDate":
        return formattedDate;
      case "startDate":
        return formattedDate;
      default:
        const { id, ...rest } = value;
        return JSON.stringify(rest);
    }
  }

  return value?.replace(/<\/?[^>]+(>|$)/g, "");
};
const renderChangeRow = (key, label, oldValue, newValue, t) => (
  <Box key={key} sx={{ mb: 1 }}>
    <Typography variant="subtitle1" fontWeight="bold">
      {t(capitalizeFirstLetter(label))}
    </Typography>
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="body2" color="#e57373">
        {truncateString(oldValue, t)}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        &rarr; {/* This html symbol represents an arrow →*/}
      </Typography>
      <Typography variant="body2" color="#81c784">
        {truncateString(newValue, t)}
      </Typography>
    </Stack>
  </Box>
);

const renderTreatmentChanges = (change, t) => {
  const oldValue = change.oldValue;
  const newValue = change.newValue;

  const oldVacs = getVaccinations(oldValue);
  const newVacs = getVaccinations(newValue);
  const oldDesc = stripHtml(oldValue?.description);
  const newDesc = stripHtml(newValue?.description);

  const rows = [];

  // 1. Show Description Change
  if (oldDesc || newDesc) {
    rows.push(
      renderChangeRow(
        "treatment-desc",
        "Treatment",
        truncateString(oldDesc, t),
        truncateString(newDesc, t),
        t,
      ),
    );
  }

  // 2. Show Vaccination Count Change
  if (oldVacs.length > 0 || newVacs.length > 0) {
    const oldTxt =
      oldVacs.length > 0
        ? t(
            oldVacs.length === 1
              ? "AddedVaccinationsCount"
              : "AddedVaccinationsCountPlural",
            { count: oldVacs.length },
          )
        : t("Nothing");
    const newTxt =
      newVacs.length > 0
        ? t(
            newVacs.length === 1
              ? "AddedVaccinationsCount"
              : "AddedVaccinationsCountPlural",
            { count: newVacs.length },
          )
        : t("Nothing");

    rows.push(
      renderChangeRow("treatment-vacs", "Vaccinations", oldTxt, newTxt, t),
    );
  }

  return rows;
};

function ChangesDisplay({ field, change, t }) {
  if (field === "treatment") {
    return renderTreatmentChanges(change, t);
  }

  const oldValue = formatChange(field, change.oldValue);
  const newValue = formatChange(field, change.newValue);
  const title = field === "priceList" ? "Service" : field;

  // Only render if there is actual change data to show
  if (oldValue !== t("Nothing") && newValue !== t("Nothing")) {
    return renderChangeRow(field, title, oldValue, newValue, t);
  }

  return null;
}

export default function AppointmentDeltas({}) {
  const { id } = useParams();
  const {
    data: deltas,
    isLoading,
    error,
  } = useQuery({
    queryKey: [APPOINTMENT_AUDIT + id],
    queryFn: () => fetchAppointment(id, "Revisions"),
    staleTime: Infinity,
  });

  const { t } = useTranslation();

  if (isLoading) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <SimpleAlert
        severity={"error"}
        message={"CouldntFetchAuditErrorMessage"}
      />
    );
  }
  return (
    <Box mt={2}>
      {deltas.map((delta) => (
        <Fragment>
          <Card
            key={delta.revisionId}
            sx={{ mb: 2, boxShadow: "none" }}
            data-testid={`appointment.details.history.revision.${delta.revisionId}`}
          >
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">
                    {delta.action === "Created" ? (
                      <Stack
                        direction={"row"}
                        spacing={2}
                        alignItems={"center"}
                      >
                        {t("Created")} <CreateOutlined />
                      </Stack>
                    ) : (
                      <Stack
                        direction={"row"}
                        spacing={2}
                        alignItems={"center"}
                      >
                        {t("Modified")} <AutoFixHigh />
                      </Stack>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} textAlign="right">
                  <Typography variant="body2" color={"GrayText"}>
                    {dayjs(delta.timestamp).format("DD/MM/YYYY HH:mm")}
                  </Typography>
                  <CreatedBy delta={delta} />
                  <AppointmentSingleRevisionDetails
                    revisionId={delta.revisionId}
                    date={dayjs(delta.timestamp).format("DD/MM/YYYY HH:mm")}
                    creator={delta.createdBy}
                    modificator={delta.modifiedBy}
                    action={delta.action}
                  />
                </Grid>
              </Grid>

              {delta.changes && (
                <Box>
                  {Object.entries(delta.changes).map(([field, change]) => (
                    <ChangesDisplay
                      key={field}
                      field={field}
                      change={change}
                      t={t}
                    />
                  ))}
                </Box>
              )}
              {delta?.usedWarehouseItems && (
                <UsedItem usageArr={delta?.usedWarehouseItems} />
              )}
            </CardContent>
          </Card>
        </Fragment>
      ))}
    </Box>
  );
}

export function UsedItem({ usageArr = [] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleNav(id) {
    if (!id) return;
    navigate(`${SINGLE_WAREHOUSE_ITEM}${id}`);
  }

  if (!usageArr || usageArr?.length == 0) {
    return;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
      >
        {t("UsedItems")}
      </Typography>
      <Stack spacing={1} divider={<Divider />} sx={{ mt: 0.75 }}>
        {usageArr.map((item) => (
          <Box
            key={item?.warehouseItemId}
            role="button"
            tabIndex={0}
            onClick={() => handleNav(item?.warehouseItemId)}
            data-testid={`appointment.details.history.used-item.${item?.warehouseItemId}`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "primary.main",
              cursor: "pointer",
              transition: "background-color 0.15s ease",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "light"
                    ? "grey.100"
                    : "rgba(255,255,255,0.06)",
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: (theme) =>
                    MUI.alpha(theme.palette.primary.main, 0.15),
                  color: "primary.main",
                }}
              >
                <EventOutlinedIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {item?.warehouseItemName}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <Chip
                label={`${t("Used")}: ${item?.amountUsed}`}
                size="small"
                color="primary"
                sx={{ fontWeight: 600 }}
                data-testid={`appointment.details.history.used-item.${item?.warehouseItemId}.used-chip`}
              />
              <ChevronRight fontSize="small" sx={{ color: "text.disabled" }} />
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function CreatedBy({ delta }) {
  const { t } = useTranslation();
  const isCreated = String(delta?.action).toUpperCase() === "CREATED";
  const label = isCreated ? t("Created by") : t("Modified by");
  const name = isCreated ? delta?.createdBy : delta?.modifiedBy;

  return (
    <Typography variant="body2">
      {`${label}: ${name ?? t("NoInfo")}`}
    </Typography>
  );
}

//Auditing gives us a string object for time, so we need to parse it manually
function formatLocalDateTime(objOrStr) {
  let obj = objOrStr;

  // If it's a string, try parsing JSON
  if (typeof objOrStr === "string") {
    try {
      obj = JSON.parse(objOrStr);
    } catch (e) {
      return null; // not JSON, can't parse
    }
  }

  // Checking if value is object
  if (!obj || typeof obj !== "object") return null;

  const year = Number(obj[0]);
  const month = Number(obj[1]) - 1; // JS months are 0-based
  const day = Number(obj[2]);
  const hour = Number(obj[3] || 0);
  const minute = Number(obj[4] || 0);
  const second = Number(obj[5] || 0);
  const nanosecond = Number(obj[6] || 0);
  const millisecond = Math.floor(nanosecond / 1_000_000);
  //Manually converting destructured string values into a new Javascript date
  const d = new Date(year, month, day, hour, minute, second, millisecond);

  //Parsing the newly created date into a date time format using dayjs
  return dayjs(d).format("DD/MM/YYYY, HH:mm");
}

function isLocalDateTimeLike(o) {
  if (!o || typeof o !== "object") return false; // If not object or missing don't do anything
  const keys = ["0", "1", "2", "3", "4", "5", "6"];
  // Returns true only if `o` has every key in `keys` and each corresponding value is numeric
  return keys.every(
    (k) => Object.prototype.hasOwnProperty.call(o, k) && !isNaN(Number(o[k])),
  );
}
